<template>
  <div class="h-full w-full relative" ref="wrap">
    <svg ref="svg" class="h-full w-full block cursor-grab active:cursor-grabbing"></svg>

    <div class="absolute top-4 left-5 pointer-events-none select-none">
      <p class="text-xs text-ink-faint max-w-xs leading-relaxed">
        Grapes cluster by shared flavour — descriptors, aroma compounds, structure.
        Click a grape to explore it; <span class="text-wine-bright">♥ the ones you love</span> and
        <span class="text-gold-bright">gold halos</span> suggest what to try next.
      </p>
      <div class="flex items-center gap-3 mt-2 text-[0.65rem] text-ink-faint uppercase tracking-[0.15em]">
        <span class="normal-case tracking-normal">berry colour:</span>
        <span class="flex items-center gap-1"><span class="w-2 h-2 rounded-full inline-block" :style="{ background: BERRY_COLORS.red }" /> noir</span>
        <span class="flex items-center gap-1"><span class="w-2 h-2 rounded-full inline-block" :style="{ background: BERRY_COLORS.gris }" /> gris</span>
        <span class="flex items-center gap-1"><span class="w-2 h-2 rounded-full inline-block" :style="{ background: BERRY_COLORS.white }" /> blanc</span>
      </div>
    </div>

    <div v-if="topRecos.length" class="absolute bottom-4 left-5 panel px-4 py-3 max-w-xs">
      <p class="text-[0.65rem] uppercase tracking-[0.18em] text-gold-bright mb-2">Worth hunting down</p>
      <div class="flex flex-wrap gap-1.5">
        <button v-for="r in topRecos" :key="r.slug" @click="selectGrape(r.slug)"
          class="chip gold hover:bg-gold/15 cursor-pointer">{{ bySlug.get(r.slug)?.name }}</button>
      </div>
    </div>

    <div v-if="hover" class="absolute panel px-3 py-2 pointer-events-none z-40 max-w-60"
      :style="{ left: hover.x + 14 + 'px', top: hover.y + 10 + 'px' }">
      <p class="font-display text-base leading-tight">{{ hover.g.name }}</p>
      <p class="text-xs text-ink-dim mt-0.5">{{ (hover.g.descriptors || []).slice(0, 4).join(" · ") }}</p>
      <p v-if="hoverShared.length" class="text-xs mt-1 text-gold-bright">
        shares with {{ selectedName }}: {{ hoverShared.join(", ") }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue"
import * as d3 from "d3"
import { store, bySlug, selectGrape, recoScores, topRecos, berryColor, BERRY_COLORS } from "../store"

const wrap = ref(null)
const svg = ref(null)
const hover = ref(null)
let sim = null

const selectedName = computed(() => bySlug.value.get(store.selectedSlug)?.name)
const hoverShared = computed(() => {
  const sel = bySlug.value.get(store.selectedSlug)
  if (!sel || !hover.value || hover.value.g.slug === sel.slug) return []
  const mine = new Set(sel.descriptors || [])
  return (hover.value.g.descriptors || []).filter(d => mine.has(d)).map(d => d.replace(/-/g, " ")).slice(0, 6)
})

const GOLD = "#e6c65c"

function draw() {
  const el = svg.value
  const { width, height } = wrap.value.getBoundingClientRect()
  const root = d3.select(el)
  root.selectAll("*").remove()

  const nodes = store.grapes.filter(g => !g.ancestor_only).map(g => ({ ...g, id: g.slug }))
  const links = store.edges.map(e => ({ ...e }))

  const degree = new Map()
  links.forEach(l => {
    degree.set(l.source, (degree.get(l.source) || 0) + 1)
    degree.set(l.target, (degree.get(l.target) || 0) + 1)
  })

  const g = root.append("g")
  const zoom = d3.zoom().scaleExtent([0.3, 4]).on("zoom", ev => g.attr("transform", ev.transform))
  root.call(zoom)
  root.on("click", () => selectGrape(null))

  const link = g.append("g").selectAll("line").data(links).join("line")
    .attr("stroke", "#5a3644")
    .attr("stroke-opacity", d => 0.15 + d.weight * 0.7)
    .attr("stroke-width", d => 0.5 + d.weight * 2.4)

  const node = g.append("g").selectAll("g").data(nodes).join("g")
    .style("cursor", "pointer")
    .call(d3.drag()
      .on("start", (ev, d) => { if (!ev.active) sim.alphaTarget(0.25).restart(); d.fx = d.x; d.fy = d.y })
      .on("drag", (ev, d) => { d.fx = ev.x; d.fy = ev.y })
      .on("end", (ev, d) => { if (!ev.active) sim.alphaTarget(0); d.fx = null; d.fy = null }))

  node.append("circle")
    .attr("class", "halo")
    .attr("r", d => nodeR(d, degree) + 5)
    .attr("fill", "none")
    .attr("stroke-width", 2)

  node.append("circle")
    .attr("class", "body")
    .attr("r", d => nodeR(d, degree))
    .attr("fill", d => berryColor(d.color))
    .attr("fill-opacity", 0.85)
    .attr("stroke", "#120a0f")
    .attr("stroke-width", 1.2)

  node.append("text")
    .text(d => d.name)
    .attr("dy", d => nodeR(d, degree) + 13)
    .attr("text-anchor", "middle")
    .attr("font-size", "10px")
    .attr("fill", "#b39c8c")
    .style("pointer-events", "none")
    .style("font-family", "Inter, sans-serif")

  node.on("click", (ev, d) => { ev.stopPropagation(); selectGrape(d.slug) })
    .on("mousemove", (ev, d) => {
      const r = wrap.value.getBoundingClientRect()
      hover.value = { g: d, x: ev.clientX - r.left, y: ev.clientY - r.top }
    })
    .on("mouseleave", () => hover.value = null)

  let fitted = false
  const fit = () => {
    if (fitted || !nodes.length) return
    fitted = true
    const xs = nodes.map(d => d.x), ys = nodes.map(d => d.y)
    const [x0, x1] = [Math.min(...xs) - 60, Math.max(...xs) + 60]
    const [y0, y1] = [Math.min(...ys) - 60, Math.max(...ys) + 60]
    const k = Math.min(2, 0.95 / Math.max((x1 - x0) / width, (y1 - y0) / height))
    const t = d3.zoomIdentity
      .translate(width / 2 - k * (x0 + x1) / 2, height / 2 - k * (y0 + y1) / 2)
      .scale(k)
    root.transition().duration(700).call(zoom.transform, t)
  }

  sim = d3.forceSimulation(nodes)
    .force("link", d3.forceLink(links).id(d => d.id).distance(d => 55 + (1 - d.weight) * 100).strength(d => d.weight * 0.9))
    .force("charge", d3.forceManyBody().strength(-150))
    .force("x", d3.forceX(width / 2).strength(0.07))
    .force("y", d3.forceY(height / 2).strength(0.10))
    .force("collide", d3.forceCollide().radius(d => nodeR(d, degree) + 13))
    .on("tick", () => {
      link.attr("x1", d => d.source.x).attr("y1", d => d.source.y)
          .attr("x2", d => d.target.x).attr("y2", d => d.target.y)
      node.attr("transform", d => `translate(${d.x},${d.y})`)
      if (!fitted && sim.alpha() < 0.06) fit()
    })
    .on("end", fit)

  draw.node = node
  draw.link = link
  restyle(node)
}

function nodeR(d, degree) {
  return 6 + Math.min(10, (degree.get(d.slug) || 1) * 1.1)
}

// BFS distances from the selected grape over the similarity edges.
// Returns null when nothing (or an off-network ancestor) is selected.
function distMap() {
  const sel = store.selectedSlug
  const grape = sel && bySlug.value.get(sel)
  if (!grape || grape.ancestor_only) return null
  const adj = new Map()
  for (const e of store.edges) {
    if (!adj.has(e.source)) adj.set(e.source, [])
    if (!adj.has(e.target)) adj.set(e.target, [])
    adj.get(e.source).push(e.target)
    adj.get(e.target).push(e.source)
  }
  const dist = new Map([[sel, 0]])
  let frontier = [sel], d = 0
  while (frontier.length && d < 3) {
    d++
    const next = []
    for (const u of frontier) for (const v of adj.get(u) || []) {
      if (!dist.has(v)) { dist.set(v, d); next.push(v) }
    }
    frontier = next
  }
  return dist
}

const fadeFor = (dist, slug, steps) => {
  if (!dist) return steps[1]
  const d = dist.has(slug) ? Math.min(dist.get(slug), steps.length - 1) : steps.length - 1
  return steps[d]
}

// re-style likes / selection / recommendations / focus-fade without re-running the sim
function restyle(nodeSel) {
  const sel = nodeSel || draw.node
  if (!sel) return
  const recos = recoScores.value
  const maxReco = Math.max(0.0001, ...recos.values())
  const dist = distMap()
  const focused = store.selectedSlug

  sel.select("circle.halo")
    .attr("stroke", d => {
      if (d.slug === focused) return "#f4eadc"
      if (store.likes.has(d.slug)) return "#d9636f"
      if (recos.has(d.slug)) return GOLD
      return "none"
    })
    .attr("stroke-opacity", d => {
      const base = recos.has(d.slug) && !store.likes.has(d.slug)
        ? 0.35 + 0.65 * (recos.get(d.slug) / maxReco) : 0.9
      return base * fadeFor(dist, d.slug, [1, 1, 0.4, 0.15])
    })
    .attr("class", d => recos.has(d.slug) && !store.likes.has(d.slug) && d.slug !== focused
      ? "halo reco-glow" : "halo")

  sel.select("circle.body")
    .attr("fill-opacity", d => fadeFor(dist, d.slug, [1, 0.92, 0.38, 0.10]))
    .attr("stroke-opacity", d => fadeFor(dist, d.slug, [1, 0.9, 0.35, 0.10]))

  sel.select("text")
    .attr("fill", d => dist && dist.get(d.slug) === 0 ? "#f4eadc" : "#b39c8c")
    .attr("fill-opacity", d => fadeFor(dist, d.slug, [1, 0.95, 0.30, 0.06]))
    .attr("font-weight", d => dist && dist.get(d.slug) === 0 ? 600 : 400)

  if (draw.link) {
    const incident = e => dist && (e.source.id === focused || e.target.id === focused)
    const nearby = e => dist && dist.get(e.source.id) <= 1 && dist.get(e.target.id) <= 1
    draw.link
      .attr("stroke", e => incident(e) ? "#d9636f" : "#5a3644")
      .attr("stroke-opacity", e => {
        if (!dist) return 0.15 + e.weight * 0.7
        if (incident(e)) return 0.55 + e.weight * 0.45
        if (nearby(e)) return 0.12
        return 0.03
      })
      .attr("stroke-width", e => incident(e) ? 1 + e.weight * 3.2 : 0.5 + e.weight * 2.4)
  }
}

watch(() => [store.selectedSlug, [...store.likes].join()], () => restyle(), { deep: false })

let ro = null
onMounted(() => {
  draw()
  ro = new ResizeObserver(() => { sim?.stop(); draw() })
  ro.observe(wrap.value)
})
onBeforeUnmount(() => { sim?.stop(); ro?.disconnect() })
</script>
